import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'

import ActivityEvent from '@components/myScores/ActivityEvent'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import Dropdown from '@components/shared/form/Dropdown'
import { usePageTitle } from '@providers/LayoutProvider'
import { useSession } from '@stores/globalData/globalData.store'
import ActivitySourceEventsIterator from '@utils/iterators/ActivitySourceEvents.iterator'
import ActivityStreamEventsIterator from '@utils/iterators/ActivityStreamEvents.iterator'

import type { Event } from '@custom-types/activity'

const BATCH_SIZE = 20

const ActivityPage: React.FC = () => {
  const session = useSession()
  const { t } = useTranslation()

  usePageTitle(t('titles.news'))

  const [events, setEvents] = useState<Readonly<Event[]>>([])
  const [filter, setFilter] = useState('allNews')
  const [iterator, setIterator] =
    useState<Readonly<ActivityStreamEventsIterator | ActivitySourceEventsIterator>>()
  const [loading, setLoading] = useState(false)
  const [remaining, setRemaining] = useState(false)

  const fetchEvents = async () => {
    if (loading || !iterator) return

    setLoading(true)
    const maxEvents = events.length + BATCH_SIZE

    let event
    while ((event = await iterator.next())) {
      setEvents((prevEvents) => [...prevEvents, event])
      if (events.length + 1 >= maxEvents) break
    }
    setLoading(false)
    if (!event) setRemaining(false)
  }

  const clear = () => {
    setEvents([])
    if (iterator) iterator.stop()
    setIterator(createIterator(filter, session.current_user?.external_id))
    setLoading(false)
    setRemaining(true)
  }

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      await fetchEvents()
    },
    { triggerOnNoScroll: false }
  )

  // When filter changes, clear any data
  useEffect(() => {
    clear()
  }, [filter])

  // When iterator changes, begin fetching again
  useEffect(() => {
    fetchEvents()

    return () => {
      iterator?.stop()
    }
  }, [iterator])

  return (
    <>
      <div className="flex flex-row justify-end w-full h-16 items-center">
        <Dropdown
          autoWidth
          variant="sm"
          name="filter"
          borderless
          onChange={(e) => setFilter(e.target.value)}
          className="text-base"
          value={filter}>
          <option value="allNews">{t('filter.allNews')}</option>
          <option value="youNews">{t('filter.youNews')}</option>
          <option value="followNews">{t('filter.followNews')}</option>
        </Dropdown>
      </div>
      <div className="flex flex-col flex-1 overflow-y-auto mb-4" ref={scrollRef}>
        {events.length === 0 && !loading && !remaining && (
          <NonIdealState title={t('content.empty.activity.title')} icon="bell" />
        )}
        {events.map((event) => (
          <ActivityEvent event={event} key={event.event_id} />
        ))}
        {(loading || remaining) && <LoadingSpinner />}
      </div>
    </>
  )
}

export default ActivityPage

function createIterator(filter: string, userId?: string) {
  switch (filter) {
    case 'youNews':
      // Return the private "personal" source for the user
      return new ActivitySourceEventsIterator(`personal_user:${userId}`)
    case 'followNews':
      // Return the activity stream iterator that doesn't include the "personal" source
      return new ActivityStreamEventsIterator()
    default:
      // Return the activity stream iterator that includes the "personal" source
      return new ActivityStreamEventsIterator({
        personalSource: `personal_user:${userId}`,
      })
  }
}
